import { AccordionItem } from '@e3dc-react/shell/Elements/Accordion'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { zodResolver } from '@hookform/resolvers/zod'
import { Box, Grid } from '@mui/material'
import React, { useCallback, useMemo } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { SettingsBox } from 'src/Elements/Data'
import SettingsFormContainer from 'src/Libs/form/components/SettingsFormContainer'
import { NumberField, Select } from 'src/Libs/inputs/react-hook-form'
import { UnitTypes } from 'src/Libs/types/common'
import useGridAndSystemProtectionMutation from '../../hooks/use-grid-and-system-protection-mutation'
import useSyncFormWithValues from '../../hooks/use-sync-form-with-values'
import { FUNCTION_NUMBER } from '../../types/common'
import { ActivePowerSettingsModel } from '../../types/grid-and-system-protection.model'
import { ActivePowerSettingsForm, getActivePowerSettingsSchema } from '../../types/schemas'
import { getChangedFieldStyle, isFieldDisabled } from '../../utils'
import ActivePowerSettingsTable from './ActivePowerSettingsTable'
import { NAME_SPACE } from './constants'
import { getDefaultValues, useAvailableFunctionOptions } from './helpers'
import { useInstallationAccessRightsContext } from 'src/Libs/installation-rights-management/provider/InstallationAccessRightsContext'
import { useFarmContext } from 'src/Elements/Farm'
type Props = {
  activePowerSettingsModel: ActivePowerSettingsModel
}

const ActivePowerSettings: React.FC<Props> = ({ activePowerSettingsModel }) => {
  const { t } = useCustomTranslation()
  const { isFarm, isFarmMasterSelected } = useFarmContext()
  const { hasInstallationWritingRights } = useInstallationAccessRightsContext()

  const canEdit = isFarm ? hasInstallationWritingRights && isFarmMasterSelected : hasInstallationWritingRights
  const defaultValues = useMemo(() => getDefaultValues(activePowerSettingsModel), [activePowerSettingsModel])
  const methods = useForm<ActivePowerSettingsForm>({
    resolver: zodResolver(getActivePowerSettingsSchema(activePowerSettingsModel, t)),
    defaultValues,
    mode: 'onBlur',
  })
  useSyncFormWithValues(methods.reset, defaultValues)

  const { updateGridAndSystemProtection, isLoading } = useGridAndSystemProtectionMutation()

  const availableFunctions = useAvailableFunctionOptions(activePowerSettingsModel.fulcrums)

  const onSubmit = useCallback(
    async (data: ActivePowerSettingsForm): Promise<void> => {
      try {
        await updateGridAndSystemProtection({ activePowerSettings: data }, FUNCTION_NUMBER.activePowerSettings)
        methods.reset(data)
      } catch (error) {
        console.error('Error updating active power settings:', error)
        methods.reset()
      }
    },
    [methods, updateGridAndSystemProtection]
  )

  return (
    <FormProvider {...methods}>
      <Grid item xs={12}>
        <AccordionItem title={t(`${NAME_SPACE}.title`)} accordionDetailsSx={{ padding: 0 }}>
          <Box component="form" onSubmit={methods.handleSubmit(onSubmit)}>
            <SettingsFormContainer onReset={methods.reset} isLoading={isLoading}>
              {availableFunctions.length > 0 && (
                <SettingsBox
                  title={t(`${NAME_SPACE}.function-active-power.title`)}
                  suffix={<Select name="selectedFunction" options={availableFunctions} disabled={!canEdit} />}
                />
              )}

              <SettingsBox
                title={t(`${NAME_SPACE}.three-tau.title`)}
                suffix={
                  <NumberField
                    name="threeTau"
                    suffix={UnitTypes.second}
                    disabled={!canEdit || isFieldDisabled(activePowerSettingsModel.threeTau)}
                    sx={getChangedFieldStyle(activePowerSettingsModel.threeTau.changed)}
                  />
                }
              />

              <ActivePowerSettingsTable activePowerSettingsModel={activePowerSettingsModel} />
            </SettingsFormContainer>
          </Box>
        </AccordionItem>
      </Grid>
    </FormProvider>
  )
}

export default ActivePowerSettings
